<template>
  <v-card 
    :height="`${canvasHeight}px`"
    :max-width="`${canvasWidth}px`"
    :elevation="borderColor || multiSelectMode ? 0 : 2"
    :style="{
      margin: 'auto', 
      transition: 'all 0.2s ease-in-out', outline: (borderColor || multiSelectMode ? `4px ${multiSelectMode ? 'dashed' : 'solid'} ${multiSelectMode ? 'forestgreen' : borderColor}` : '')
    }"
  >
    <grid-template
      :rows="gridRows || (isStatic ? 0 : 5)"
      :cols="gridCols || (isStatic ? 0 : 10)"
      :canvas-width="canvasWidth"
      :canvas-height="canvasHeight"
      :overlay-opacity="gridCols + gridRows === 0 ? '100%' : '80%'"
    >
      <stage 
        ref="stage"
        v-bind="{isStatic}"
        layers="4"
        :config="stageConfiguration"
        @click:stage="$emit('select-item')"
        @transform:end="transformEnd"
        @drag:start="dragStart($event, {cursor: 'grabbing', stageKey: $refs.stage.stageKey})"
        @drag:end="(event, transformerTargetName) => {dragEnd(event, transformerTargetName, {stageKey: $refs.stage.stageKey})}"
      >
        <template #layer-1="{ stageKey }">
          <template v-for="(shape, index) in shapes">
            <draw-circle 
              v-if="shape.shape === 'CIRCLE'"
              :key="`layer-1-circle-element-${index}`"
              v-bind="shape"
              :color="shape.fill"
              :border-width="shape.strokeWidth"
              :border-color="shape.stroke"
              :draggable="!disableDrag"
              :label-scale="shape.textScale"
              :text-color="shape.textFill"
              zero-rotation-label
              @mouse:over="setCursor(stageKey, isStatic ? 'default' : 'grab')"
              @mouse:down="() => {
                setCursor(stageKey, isStatic ? 'default' : 'grabbing')
                $emit('select-item', 'shapes', index)
              }"
              @mouse:up="setCursor(stageKey, isStatic ? 'default' : 'grab')"
              @mouse:out="setCursor(stageKey)"
            />
            <draw-box 
              v-else-if="shape.shape === 'BOX'"
              :key="`layer-1-box-element-${index}`"
              v-bind="shape"
              :draggable="!disableDrag"
              :border-width="shape.strokeWidth"
              :border-color="shape.stroke"
              :color="shape.fill"
              :label-scale="shape.textScale"
              :text-color="shape.textFill"
              @mouse:over="setCursor(stageKey, isStatic ? 'default' : 'grab')"
              @mouse:down="() => {
                setCursor(stageKey, isStatic ? 'default' : 'grabbing')
                $emit('select-item', 'shapes', index)
              }"
              @mouse:up="setCursor(stageKey, isStatic ? 'default' : 'grab')"
              @mouse:out="setCursor(stageKey)"
            />
          </template>
        </template>
        <template #layer-2>
          <template v-if="!!Object.keys(hoveredTablesMap).length">
            <draw-line
              v-for="(line, lineIndex) of getLinkPoints(Object.keys(hoveredTablesMap).map(r => tablesMap[r]))"
              :key="`hover-line-${lineIndex}`"
              :points="line.points"
            />
          </template>
          <template v-if="!!Object.keys(selectedReservation || {}).length">
            <draw-line 
              v-for="(line, lineIndex) of getLinkPoints(selectedTablesList)"
              :key="'selected-line-' + lineIndex"
              :points="line.points"
            />
          </template>
        </template>
        <template #layer-4="{ stageKey }">
          <template v-for="(table, index) in shownTables">
            <draw-box 
              v-if="
                selectReservation && (
                  selectedTables.includes(table.tableId) 
                  || selectedTable.tableId === table.tableId || (!!hoveredTablesMap[table.tableId] 
                    || !!selectedOpenTablesMap[table.tableId] && multiSelectMode)
                )"
              :key="`table-${index}-selected-border`"
              v-bind="table"
              color="rgb(108, 252, 122, 0.2)"
              :border-dashed="multiSelectMode && !!hoveredTablesMap[table.tableId] && !selectedOpenTablesMap[table.tableId]"
              :border-color="multiSelectMode ? 'rgb(108, 252, 122, 0.55)' : 'blue'"
              border-width="2"
              corner-radius="5"
              :draggable="!disableDrag"
            />
            <draw-table 
              v-if="reservationsMap[table.tableId] && locationTablesMap[table.tableId] && !!getColorTable(locationTablesMap[table.tableId], table.shape) && !!getColorTable(table)"
              :key="`reserved-table-${index}`"
              :location-table="locationTablesMap[table.tableId]"
              :config-table="table"
              :image="getColorTable(showReservationsAt >= 0 ? reservationsMap[table.tableId] : locationTablesMap[table.tableId], table.shape)"
              :text="getTableText(table)"
              :text-color="getTableTextColor(showReservationsAt >= 0 ? reservationsMap[table.tableId] : locationTablesMap[table.tableId])"
              :draggable="!disableDrag"
              :floating-box-color="getTableFillColor(showReservationsAt >= 0 ? reservationsMap[table.tableId] : locationTablesMap[table.tableId])"
              :text-background-color="getTableFillColor(showReservationsAt >= 0 ? reservationsMap[table.tableId] : locationTablesMap[table.tableId])"
              :server-initial="reservationsMap[table.tableId].serverInitials"
              :server-initial-color="getTableTextColor(showReservationsAt >= 0 ? reservationsMap[table.tableId] : locationTablesMap[table.tableId])"
              :server-color="(reservationsMap[table.tableId] || {}).serverColor"
              :server-text-color="(reservationsMap[table.tableId] || {}).serverTextColor"
              :status-icon="getStatusIcon(showReservationsAt >= 0 ? reservationsMap[table.tableId] : locationTablesMap[table.tableId])"
              zero-rotation-label
              @mouse:down="(event, table) => {
                setCursor(stageKey, isStatic ? 'pointer' : 'grabbing')
                if (multiSelectMode) {
                  $emit('select-open-table', locationTablesMap[table.tableId])
                } else if (selectReservation && !!(table || {}).id) {
                  const {reservation, tableIds} = reservationsMap[table.tableId]
                  $emit('select-reservation', reservation, tableIds)
                }
              }"
              @mouse:over="() => {
                setCursor(stageKey, 'pointer')
                hoveredTablesMap = reservationsMap[table.tableId].tableIds.reduce((acc, t) => {
                  if ((tablesMap[t] || {}).showOnFloorPlan)
                    acc[t] = minutesToDisplay((reservationsMap[table.tableId] || {}).time || 0)
                  return acc
                }, {}) || {}
              }"
              @mouse:out="() => {
                setCursor(stageKey)
                hoveredTablesMap = {}
              }"
            />
            <draw-table 
              v-else-if="!!tableImagesMap[table.shape] && locationTablesMap[table.tableId] && !!getColorTable(locationTablesMap[table.tableId], table.shape)"
              :key="`empty-table-${index}`"
              :location-table="locationTablesMap[table.tableId]"
              :config-table="table"
              :image="getColorTable(showReservationsAt >= 0 ? {} : locationTablesMap[table.tableId], table.shape)"
              :text-color="getTableTextColor(showReservationsAt >= 0 ? {} : locationTablesMap[table.tableId])"
              :floating-box-color="getTableFillColor(showReservationsAt >= 0 ? {} : locationTablesMap[table.tableId])"
              :text-background-color="getTableFillColor(showReservationsAt >= 0 ? {} : locationTablesMap[table.tableId])"
              :status-icon="getStatusIcon(showReservationsAt >= 0 ? {} : locationTablesMap[table.tableId])"
              :text="hoveredTablesMap[table.tableId] && isStatic ? hoveredTablesMap[table.tableId] : table.tableName"
              :server-initial="selectReservation ? (serverAssignmentsMap[table.tableId] || {}).initials : 'FT'"
              :server-initial-color="getTableTextColor(showReservationsAt >= 0 ? {} : locationTablesMap[table.tableId])"
              :server-color="(serverAssignmentsMap[table.tableId] || {}).color"
              :server-text-color="(serverAssignmentsMap[table.tableId] || {}).textColor"
              :show-server-assignment="showServerAssignment && !selectReservation"
              zero-rotation-label
              :draggable="!disableDrag"
              :hide-server-box="selectReservation && !showServerAssignment"
              :hide-status-box="!(locationTablesMap[table.tableId] || {}).status"
              @mouse:over="() => {
                setCursor(stageKey, isStatic ? 'pointer' : 'grab')
                const temp = {[table.tableId]: multiSelectMode ? table.tableName : '+'}
                hoveredTablesMap = temp
              }"
              @mouse:down="() => {
                setCursor(stageKey, isStatic ? 'pointer' : 'grabbing')
                if (selectReservation) $emit('select-open-table', locationTablesMap[table.tableId])
                else $emit('select-item', 'tables', index)
              }"
              @mouse:up="setCursor(stageKey, isStatic ? 'pointer' : 'grab')"
              @mouse:out="() => {
                setCursor(stageKey)
                hoveredTablesMap = {}
              }"
            />
            <draw-table 
              v-else-if="table._localAddition"
              :key="`new-addition-table-${index}`"
              :config-table="table"
              :image="getColorTable(table, table.shape)"
              :text="table.tableName"
              server-initial="FT"
              :status-icon="statusIcons.alert"
              floating-box-color="#FFC107"
              :show-server-assignment="showServerAssignment && !selectReservation"
              :draggable="!disableDrag"
              zero-rotation-label
              @mouse:over="() => setCursor(stageKey, isStatic ? 'pointer' : 'grab')"
              @mouse:down="() => {
                setCursor(stageKey, isStatic ? 'pointer' : 'grabbing')
                $emit('select-item', 'tables', index)
              }"
              @mouse:up="() => setCursor(stageKey, isStatic ? 'pointer' : 'grab')"
              @mouse:out="() => setCursor(stageKey)"
            />
          </template>
        </template>
        <template #layer-3="{ stageKey }">
          <draw-text 
            v-for="(label, index) in labels"
            :key="`label-${label.name}`"
            v-bind="label"
            :color="label.fill"
            :scale="label.scaleX"
            :draggable="!disableDrag"
            :allow-transformer-stretch="false"
            @mouse:over="setCursor(stageKey, isStatic ? 'default' : 'grab')"
            @mouse:down="() => {
              setCursor(stageKey, isStatic ? 'default' : 'grabbing')
              $emit('select-item', 'labels', index)
            }"
            @mouse:up="setCursor(stageKey, isStatic ? 'default' : 'grab')"
            @mouse:out="setCursor(stageKey)"
          />
          <template v-for="(icon, index) in icons">
            <draw-image 
              v-if="!!customIconsMap[`${icon.color}-${icon.icon}.png`] || !!arrowIconsMap[`${icon.color}-${icon.icon}.png`]"
              :key="`icon-${icon.name}`"
              v-bind="icon"
              :color="null"
              :image="customIconsMap[`${icon.color}-${icon.icon}.png`] || arrowIconsMap[`${icon.color}-${icon.icon}.png`]"
              :draggable="!disableDrag"
              :allow-transformer-stretch="false"
              @mouse:over="setCursor(stageKey, isStatic ? 'default' : 'grab')"
              @mouse:down="() => {
                setCursor(stageKey, isStatic ? 'default' : 'grabbing')
                $emit('select-item', 'icons', index)
              }"
              @mouse:up="setCursor(stageKey, isStatic ? 'default' : 'grab')"
              @mouse:out="setCursor(stageKey)"
            />
          </template>
        </template>
      </stage>
    </grid-template>
  </v-card>
</template>

<script>
import canvas from '@/ft-vue-core/mixins/canvas'
import canvasHelpers from './mixins/canvasHelpers'
import DrawTable from './canvas/DrawTable'
import floorPlanTimeHelpers from '../../../floorPlan/mixins/floorPlanTimeHelpers'
function* getRefreshKey () {
  for (let i = 1; ; i++) yield 'refresh-key-' + i
}
export default {
  name: 'FloorPlanCanvas',
  components: {DrawTable},
  mixins: [canvas, canvasHelpers, floorPlanTimeHelpers],
  props: {
    tables: {
      type: Array,
      default () {return []}
    },
    shapes: {
      type: Array,
      default () {return []}
    },
    labels: {
      type: Array,
      default () {return []}
    },
    icons: {
      type: Array,
      default () {return []}
    },
    stageConfig: Object,
    isStatic: Boolean,
    transformerConfig: Object,
    disableDrag: Boolean,
    gridDensity: {
      type: Number,
      default: 0
    },
    autoClearReservationStatus: Boolean,
    serverAssignmentsMap: {
      type: Object,
      default () {return {}}
    },
    showReservationsAt: {
      type: [Number, String],
      validator (value) {return !isNaN(value) || value == null}
    },
    reservations: {
      type: [Array, Object],
      default () {return []}
    },
    selectReservation: Boolean,
    selectedReservation: {
      type: Object,
      default () {return {}}
    },
    selectedOpenTables: {
      type: Array,
      default () {return []}
    },
    /** Flag to remove table statuses from queried list.
     * 
     * @see canvasHelpers
     */
    excludeStatus: Boolean,
    // selectedTable: {
    //   type: Object,
    //   default () {return {}}
    // },
    reservationXRay: Boolean,
    showServerAssignment: Boolean,
    canvasHeight: {
      type: Number,
      default: 500
    },
    canvasWidth: {
      type: Number,
      default: 1000
    },
    borderColor: String,
    multiSelectMode: Boolean,
  },
  data () {
    return {
      hoveredTablesMap: {},
      refreshKeyGenerator: getRefreshKey(),
      tableRefreshKey: 0,
      allowTransformerSkew: false,
      transformerConfiguration: {
        enabledAnchors: ['top-left', 'top-right', 'bottom-right', 'bottom-left'],
        flipEnabled: false
      }
    }
  },
  computed: {
    stageConfiguration () {
      if (this.stageConfig) return this.stageConfig
      return {
        name: 'stage',
        height: this.canvasHeight,
        width: this.canvasWidth
      }
    },
    gridRows () {
      return this.gridDensity * (this.canvasHeight / 100 - this.canvasHeight % 100)
    },
    gridCols () {
      return this.gridDensity * (this.canvasWidth / 100 - this.canvasWidth % 100)
    },
    tablesMap () {
      return this.tables.reduce((acc, t) => {
        acc[t.tableId] = t
        return acc
      }, {})
    },
    selectedTable () {
      if (this.selectedOpenTables?.length === 1) return this.selectedOpenTables[0]
      return {}
    },
    selectedOpenTablesMap () {
      return this.selectedOpenTables?.reduce((acc, r, index) => {
        if (!r) return acc
        r._index = index
        acc[r.id] = r
        return acc
      }, {}) ?? {}
    },
    currentTime () {
      let clubMoment = this.$ftAPI().clubMoment().format('H-mm').split('-')?.map(e => parseInt(e)) ?? [0, 0]
      return clubMoment[0] * 60 + clubMoment[1]
    },
    nearestReservationTime () {
      let index = -1
      Object.keys(this.reservations ?? {})?.forEach((r, i) => {
        if (this.currentTime < parseInt(r)) {
          index = i - 1
          return
        }
      })
      if (index === -1) index = Object.keys(this.reservations).length - 1
      return parseInt(Object.keys(this.reservations ?? {})?.[index] ?? 0)
    },
    reservationsMap () {
      if (this.showReservationsAt === -1) {
        let reservationsMap = {}
        for (let reservationTime of Object.entries(this.reservations).sort((a, b) => parseInt(a[0]) >= parseInt(b[0]))) {
          const reservations = reservationTime[1] // The 'value' of Object.entries()
          for (const reservationInfo of reservations) {
            if (reservationInfo.endTime <= this.currentTime || reservationInfo.preStartTime > this.currentTime) continue
            for (const tableId of reservationInfo.tableIds) {
              if (!reservationsMap[tableId]) reservationsMap[tableId] = {...reservationInfo, links: reservationInfo.tableIds}
            }
          }
        }
        return reservationsMap
      } else {
        return this.reservations[this.showReservationsAt]?.reduce((acc, r) => {
          for (let tableId of r.tableIds ?? []) acc[tableId] = Object.assign(r, {links: r.tableIds})
          return acc
        }, {}) ?? {}
      }
    },
    tableStatusMap () {
      return null
    },
    selectedTables () {
      return this.selectedReservation.tableAssignments?.map(t => t.locationTableId) ?? []
    },
    selectedTablesList () {
      return this.selectedTables.reduce((acc, t) => {
        const table = this.tablesMap[t]
        if (table?.showOnFloorPlan) acc.push(table)
        return acc
      }, [])
    },
    selectedTablesMap () {
      return this.selectedTables.reduce((acc, t) => {
        acc[t] = true
        return acc
      }, {}) ?? {}
    },
    shownTables () {
      return this.tables?.filter(t => t.showOnFloorPlan)
    }
  },
  watch: {
    selectedTables () {
      this.$nextTick(() => this.reRenderTableLabels())
    },
    hoveredTablesMap () {
      this.$nextTick(() => this.reRenderTableLabels())
    },
    reservationXRay (newVal) {
      this.$nextTick(() => {
        if (newVal) this.reRenderTableLabels()
      })
    }
  },
  mounted () {
    if (this.transformerConfig) this.transformerConfiguration = this.transformerConfig
  },
  methods: {
    clearTransformer () {
      const stage = this.$refs.stage
      stage.clearTransformer()
    },
    setCursor: (stageKey, style = 'default') => document.querySelector(`.${stageKey}`).style.cursor = style,
    transformEnd (event) {
      const {transformerTargetName, name, scaleX, scaleY, rotation} = event.target?.attrs ?? {}
      this.updateElement({name: transformerTargetName || name, scaleX, scaleY, rotation})
    },
    dragStart (event, {...args}) {
      if (args.stageKey && args.cursor) this.setCursor(args.stageKey, args.cursor)
    },
    dragEnd (event, transformerTargetName, {...args}) {
      const {name, x, y} = event.target?.attrs ?? {}
      if (name === 'transformer') return
      this.updateElement({name: transformerTargetName, x, y})
      if (args.stageKey) this.setCursor(args.stageKey, args.cursor || 'grab')
    },
    findUpdateElement (targetAttrs) {
      for (let list of ['tables', 'shapes', 'labels', 'icons']) {
        const targetIndex = this[list]?.findIndex(e => e.name === targetAttrs.name)
        if (targetIndex !== -1) return [targetIndex, list]
      }
      return [-1, null]
    },
    updateElement (targetAttrs) {
      const [updateIndex, list] = this.findUpdateElement(targetAttrs)
      if (list) {
        this.$emit('update-element', list, updateIndex, Object.assign(this[list][updateIndex], targetAttrs))
        // this[list]?.splice(updateIndex, 1, Object.assign(this[list][updateIndex], targetAttrs)) 
      }
    },
    getTableText (table) {
      const {tableId} = table
      if (this.hoveredTablesMap[tableId]) 
        if (this.reservationsMap[tableId])
          return table.tableName
        else
          return `${this.hoveredTablesMap[tableId]}\n${this.getReserveeName(table)}`
      else if (this.reservationXRay || this.reservationsMap[tableId]) 
        return `${this.minutesToDisplay(this.reservationsMap[tableId].time)}\n${this.getReserveeName(table)}`
      else if (this.selectedTablesMap[tableId]) 
        return `${table.tableName}\n${this.getReserveeName(table)}` 
      else return table.tableName
    },
    reRenderTableLabels () {
      this.tableRefreshKey = this.refreshKeyGenerator.next().value
    },
    getReserveeName (table) {
      const reservation = this.reservationsMap[table.tableId]?.reservation
      if (!reservation?.details) return ''
      const member = reservation.details[0]?.member ?? {}
      return member.nameLast ?? ''
    },
    updateStage ({target}) {
      const {x, y, name, tableName, scaleX, scaleY, skewX, skewY, rotation} = target?.attrs ?? {}
      const targetName = name || tableName
      if (!targetName) return
      let listTarget
      if (targetName.startsWith('basic-shape')) listTarget = 'shapes'
      else if (targetName.startsWith('label')) listTarget = 'labels'
      else if (targetName.startsWith('icon')) listTarget = 'icons'
      else listTarget = 'tables'
      let updateIndex = this[listTarget]?.findIndex(e => e.name === targetName)
      if (updateIndex !== -1) 
        this.$emit('update', listTarget, updateIndex, {...this[listTarget]?.[updateIndex] ?? [], x, y, scaleX, scaleY, skewX, skewY, rotation})
    },
    clickStage ({target}) {
      if (target.name()?.includes('_anchor')) return
      if (!this.isStatic) this.setTransformer(target.attrs)
      if (this.selectReservation) {
        const {reservation, tableIds} = this.reservationsMap[target.attrs.tableId] ?? {}
        this.$emit('select-reservation', reservation, tableIds)
      }
      else {
        let listTarget
        if (target.name()?.startsWith('basic-shape')) listTarget = 'shapes'
        else if (target.name()?.startsWith('label')) listTarget = 'labels'
        else if (target.name()?.startsWith('icon')) listTarget = 'icons'
        else listTarget = 'tables'
        this.$emit('select-item', listTarget, this[listTarget].findIndex(t => t.name === target.attrs.tableName))
      }
    },
    setTransformer ({tableName}) {
      this.setTransformerAnchors(tableName?.startsWith('basic-shape'))
      const transformer = this.$refs.stage?.$refs.transformer?.getTransformer()
      const selectedNode = transformer.getStage().findOne(`.${tableName}`)
      if (selectedNode === transformer.node()) return
      transformer.nodes(selectedNode ? [selectedNode] : [])
    },
    setTransformerAnchors (allowStretch) {
      let enabledAnchors = ['top-left', 'top-right', 'bottom-right', 'bottom-left']
      if (allowStretch) enabledAnchors.push('top-center', 'middle-right', 'bottom-center', 'middle-left')
      this.$refs.stage?.setAllowStretch(allowStretch)
      this.$set(this.transformerConfiguration, 'enabledAnchors', enabledAnchors)
    },
    resetTransformer () {
      const transformer = this.$refs.stage?.$refs.transformer?.getTransformer()
      transformer.nodes([])
    },
  }
}
</script>

<style scoped>
.floor-plan-container {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: scroll;
}
.floor-plan-container div {
  position: absolute;
  top: 0px;
  left: 0px;
  z-index: 2;
}
.floor-plan-container-background {
  position: absolute;
  top: 0px;
  left: 0px;
  height: 100%;
  border-spacing: 0px;
  z-index: 1;
}
.floor-plan-container-background tr td {
  border: 1px solid grey;
}
.no-tables-configured-container {
  position: absolute;
  top: 0px;
  left: 0px;
  height: 100%;
  width: 1000px;
  display: flex;
  justify-content: center;
  align-items: center;
  user-select: none;
  color: rgba(0, 0, 0, 0.6);
}
.processing-update-container {
  position: absolute;
  top: 0px;
  left: 0px;
  height: 100%;
  width: 1000px;
  background-color: rgba(0, 0, 0, 0.3);
}
.blurry {
  filter: blur(2px);
}
</style>